import {
  DemoMemoryCalendarTools,
  DemoMemoryMeetingTools,
  DemoMemoryPresets,
  DemoMemoryProjectManagementTools,
} from '@timelyapp/integration-cloud-sdk/memory-demo-data/types';
import type { DropdownMenuItem } from '@timelyapp/react';
import { Pill } from '@timelyapp/react';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDemoModeAvailability, getTicViewDemoMode } from 'redux/modules/tic/tic.demo.selectors';
import { saveMemoryConfig } from 'redux/modules/tic/tic.memories.actions';
import type { ThunkDispatch } from 'types/redux';

const DROPDOWN_WIDTH_PX = 330 as const;

type Return = {
  dropdownItems: Array<DropdownMenuItem>;
  shouldRenderSeparateButton: boolean;
  isActive: boolean;
  isAvailable: boolean;
};

/**
 * Hook for handling demo memory options and related actions
 * Returns dropdown menu items, availability status, and helper functions
 */
export const useDemoMemoryOptions = (): Return => {
  const dispatch = useDispatch<ThunkDispatch>();
  const ticViewDemoModeAvailability = useSelector(getDemoModeAvailability);
  const ticViewDemoMode = useSelector(getTicViewDemoMode);

  const handleSettingsChange = useCallback(
    (updates: Partial<typeof ticViewDemoMode>) => {
      const updatedDemoSettings = { ...ticViewDemoMode, ...updates };
      dispatch(saveMemoryConfig({ demoSettings: updatedDemoSettings }));
    },
    [dispatch, ticViewDemoMode]
  );

  const dropdownItems = useMemo(() => {
    const items: Array<DropdownMenuItem> = [];

    if (ticViewDemoModeAvailability === 'disabled') {
      return items;
    }

    // Add main switch
    items.push({
      type: 'switch',
      label: 'Enable demo mode',
      checked: ticViewDemoMode.enabled,
      onCheckedChange: () => handleSettingsChange({ enabled: !ticViewDemoMode.enabled }),
      closeOnSelect: false,
    });

    items.push({ type: 'separator' });

    // Add presets as radio group
    items.push({
      type: 'radioGroup',
      label: 'Preset',
      currentValue: ticViewDemoMode.preset,
      onValueChange: value =>
        handleSettingsChange({ preset: value as typeof ticViewDemoMode.preset }),
      closeOnSelect: false,
      subOptions: DemoMemoryPresets.map(({ label, value }) => ({
        type: 'radioItem',
        label,
        value,
      })),
    });

    items.push({
      type: 'separator',
    });
    items.push({
      type: 'label',
      label: 'Tool Preferences',
    });

    // Add tool submenus
    items.push({
      type: 'subMenu',
      label: 'Calendar',
      width: DROPDOWN_WIDTH_PX,
      closeOnSelect: false,
      sidenote: DemoMemoryCalendarTools.find(({ value }) => value === ticViewDemoMode.calendarTool)
        ?.label,
      subOptions: [
        {
          type: 'radioGroup',
          currentValue: ticViewDemoMode.calendarTool,
          onValueChange: value =>
            handleSettingsChange({ calendarTool: value as typeof ticViewDemoMode.calendarTool }),
          label: 'Calendar Tool Options',
          closeOnSelect: false,
          subOptions: DemoMemoryCalendarTools.map(({ label, value }) => ({
            type: 'radioItem',
            label,
            value,
          })),
        },
      ],
    });

    items.push({
      type: 'subMenu',
      label: 'Meetings',
      width: DROPDOWN_WIDTH_PX,
      closeOnSelect: false,
      sidenote: DemoMemoryMeetingTools.find(({ value }) => value === ticViewDemoMode.meetingTool)
        ?.label,
      subOptions: [
        {
          type: 'radioGroup',
          currentValue: ticViewDemoMode.meetingTool,
          onValueChange: value =>
            handleSettingsChange({ meetingTool: value as typeof ticViewDemoMode.meetingTool }),
          label: 'Meeting Tool Options',
          closeOnSelect: false,
          subOptions: DemoMemoryMeetingTools.map(({ label, value }) => ({
            type: 'radioItem',
            label,
            value,
          })),
        },
      ],
    });

    items.push({
      type: 'subMenu',
      label: 'Project Management',
      width: DROPDOWN_WIDTH_PX,
      closeOnSelect: false,
      sidenote: DemoMemoryProjectManagementTools.find(
        ({ value }) => value === ticViewDemoMode.projectManagementTool
      )?.label,
      subOptions: [
        {
          type: 'radioGroup',
          currentValue: ticViewDemoMode.projectManagementTool,
          onValueChange: value =>
            handleSettingsChange({
              projectManagementTool: value as typeof ticViewDemoMode.projectManagementTool,
            }),
          label: 'Project Management Tool Options',
          closeOnSelect: false,
          subOptions: DemoMemoryProjectManagementTools.map(({ label, value }) => ({
            type: 'radioItem',
            label,
            value,
          })),
        },
      ],
    });

    return [
      {
        type: 'subMenu',
        closeOnSelect: false,
        width: DROPDOWN_WIDTH_PX,
        label: 'Demo Mode',
        sidenote: (
          <Pill size="xsmall" variant={ticViewDemoMode.enabled ? 'success' : 'subtle'}>
            {ticViewDemoMode.enabled ? 'Active' : 'Off'}
          </Pill>
        ),
        subOptions: items,
      },
    ] satisfies Array<DropdownMenuItem>;
  }, [handleSettingsChange, ticViewDemoMode, ticViewDemoModeAvailability]);

  return {
    dropdownItems,
    shouldRenderSeparateButton: ticViewDemoMode.enabled,
    isActive: ticViewDemoMode.enabled,
    isAvailable: ticViewDemoModeAvailability !== 'disabled',
  };
};

export default useDemoMemoryOptions;
